import {Crown} from 'lucide-react';
import {PropsWithChildren} from 'react';
import {useTranslation} from 'react-i18next';
import {cn, MainLoader} from '@chekin/guestapp-ui-kit';
import {GUEST_STATUSES, ShortGuest} from '@chekin/core';
import {useGuestGroup} from 'hooks';
import {GuestItem} from 'components/GuestsPanel/components/GuestItem';
import {LoaderWrapper} from './styled';

const PanelGroupTitle = ({
  children,
  className,
  isNew,
}: PropsWithChildren & {className?: string; isNew?: boolean}) => {
  return (
    <div
      className={cn(
        'text-sm font-semibold leading-6 mb-2.5 flex gap-2 items-center text-[var(--font-secondary)] uppercase',
        'panel-group-title',
        'kiosko:text-kiosko-s',
        isNew && 'w-full mb-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

type Props = {
  handleRegisterNewGuest?: (isLeader?: boolean) => void;
};

function GuestsList({handleRegisterNewGuest}: Props) {
  const {guestGroup, isLoading, isFetching} = useGuestGroup();
  const members = guestGroup?.members;
  const leaderGuest = members?.find(member => member.id === guestGroup?.leader_id);
  const accompanyingMembers =
    members?.filter(member => member.id !== guestGroup?.leader_id) ?? [];
  const knownNumberOfGuests = guestGroup?.known_number_of_guests ?? 0;

  const showAccompanyingGuests = knownNumberOfGuests > 1;

  if (isLoading) {
    return (
      <LoaderWrapper className="kiosko:w-full">
        <MainLoader visible width={80} />
      </LoaderWrapper>
    );
  }

  return (
    <GuestListNew
      accompanyingMembers={accompanyingMembers}
      knownNumberOfGuests={knownNumberOfGuests}
      showAccompanyingGuests={showAccompanyingGuests}
      leaderGuest={leaderGuest}
      isFetching={isFetching}
      onRegisterNew={() => handleRegisterNewGuest?.(members?.length === 0)}
    />
  );
}

const EmptyGuest = {
  id: '',
  document_number: '',
  is_pre_registered: false,
  email: '',
  participant_id: '',
  full_name: '',
  guestapp_status: GUEST_STATUSES.incomplete,
  isLeader: false,
};

function GuestListNew({
  accompanyingMembers,
  knownNumberOfGuests,
  showAccompanyingGuests,
  leaderGuest,
  isFetching,
  onRegisterNew,
}: {
  accompanyingMembers: ShortGuest[];
  knownNumberOfGuests: number;
  showAccompanyingGuests: boolean;
  leaderGuest?: ShortGuest;
  isFetching: boolean;
  onRegisterNew: () => void;
}) {
  const {t} = useTranslation();

  const withEmptyAccompanyingMembers = Array.from(
    {length: Math.max(accompanyingMembers.length, knownNumberOfGuests - 1)},
    (_, index) => {
      return accompanyingMembers[index] ?? EmptyGuest;
    },
  );

  return (
    <div className="w-full space-y-4 kiosko:flex kiosko:flex-col kiosko:gap-10">
      <PanelGroupTitle className="guest-register-view__group-title" isNew>
        {t('guest_in_charge')}
        <Crown
          color="#FFD166"
          fill="#FFD166"
          className="top-[-1px] relative w-5 h-5 kiosko:w-10 kiosko:h-10"
        />
      </PanelGroupTitle>

      <div className="w-full grid xl:grid-cols-2 gap-6">
        <GuestItem
          isLoading={isFetching}
          guestMember={leaderGuest ?? {...EmptyGuest, isLeader: true}}
          onRegisterNew={onRegisterNew}
        />
      </div>

      {showAccompanyingGuests && (
        <>
          <PanelGroupTitle className="guest-register-view__group-title" isNew>
            {t('accompanying_guests')}
          </PanelGroupTitle>

          <div className="w-full grid xl:grid-cols-2 gap-6">
            {withEmptyAccompanyingMembers.map((member, index) => (
              <GuestItem
                isLoading={isFetching}
                guestMember={{...member, isLeader: false}}
                key={index}
                onRegisterNew={onRegisterNew}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export {GuestsList};
